// GPU hair shade driver (Phase C). Owns the WGSL shade pipeline + buffers and
// runs one dispatch per frame, reading spec_mod's gpu-buffer output directly
// (no readback) and producing one u8-unorm RGBA word per Gaussian. The only
// GPU→CPU hop is the packed color readback (P*4 bytes, async), which pollApply
// merges straight into Spark's packed splat array — no float repack loop.

import shadeShader from './wgsl/shade.wgsl?raw';
import { mergePackedRgb } from './splatUtil.js';

const MAX_WG = 65535;

export class HairGpuShader {
  constructor() {
    this.device = null;
    this.pipeline = null;
    this.bind0 = null;
    this.bind1 = null;
    this._specBuffer = null;
    this.P = 0;
    this._u = null;          // params staging (48 bytes)
    this._uf = null;
    this._uu = null;
    this._sh27Host = new Float32Array(27);
    this._reflVersion = -1;
    // Reflectance binding source: the shader-owned bRefl (CPU 3ch upload) or an
    // ORT gpu-buffer output bound directly (1ch). bind0 is rebuilt on change.
    this._reflBound = null;
    // Single readback staging buffer (built in init()): shade() dispatches +
    // starts mapAsync without blocking; pollApply() (called every frame from
    // update()) merges it once landed. Strictly serial — HairRenderer holds the
    // next shade until this readback is applied — so one is ever in flight.
    this._rbBuf = null;
    this._rbState = 'free'; // 'free' | 'inflight' | 'ready'
    this.bStatic = null;   // stride-10 [pos3,bn3,alb3,rough]; created by initStatic
    this.ready = false;    // pipeline + buffers built (init ran)
  }

  // Create the stride-10 static-attr buffer [pos3, bn3, alb3, rough] and fill
  // pos/bn/rough. Albedo lanes (offsets 6..8) are written here from CPU data
  // only when `albedoFromData` (the CPU mat_mod unpack path); on the GPU-unpack
  // path they are left zero and the WGSL unpack pass (matModUnpacker) writes
  // them straight from mat_mod's gpu-buffer output — so this can run at load,
  // before mat_mod's result exists on the CPU. Idempotent.
  initStatic(device, data, albedoFromData) {
    if (this.bStatic) return;
    this.device = device;
    this.P = data.P;
    const P = this.P;
    const sa = new Float32Array(P * 10);
    const pos = data.positions, bn = data.baseNormal, ro = data.roughness;
    const al = albedoFromData ? data.albedo : null;
    for (let i = 0; i < P; i++) {
      const o = i * 10, i3 = i * 3;
      sa[o] = pos[i3]; sa[o + 1] = pos[i3 + 1]; sa[o + 2] = pos[i3 + 2];
      sa[o + 3] = bn[i3]; sa[o + 4] = bn[i3 + 1]; sa[o + 5] = bn[i3 + 2];
      if (al) { sa[o + 6] = al[i3]; sa[o + 7] = al[i3 + 1]; sa[o + 8] = al[i3 + 2]; }
      sa[o + 9] = ro[i];
    }
    // COPY_DST so the matModUnpacker can write the albedo lanes on the GPU path.
    this.bStatic = device.createBuffer({ size: sa.byteLength, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });
    device.queue.writeBuffer(this.bStatic, 0, sa);
  }

  // Build pipeline + the rest of the buffers. data has positions/baseNormal/
  // albedo/roughness/neuralBsdfReflectance (Float32Array); fgLut = { lut, res }.
  // bStatic is created by initStatic — lazily here (albedo from CPU data) if the
  // GPU-unpack path didn't pre-create it at load.
  init(device, data, fgLut) {
    if (this.ready) return;
    this.device = device;
    this.P = data.P;
    const P = this.P;
    if (!this.bStatic) this.initStatic(device, data, /*albedoFromData*/ true);

    this.pipeline = device.createComputePipeline({
      layout: 'auto',
      compute: { module: device.createShaderModule({ code: shadeShader }), entryPoint: 'main' },
    });

    // Params uniform (48 bytes).
    this._u = new ArrayBuffer(48);
    this._uf = new Float32Array(this._u);
    this._uu = new Uint32Array(this._u);
    this.uParams = device.createBuffer({ size: 48, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });

    this.bSh27 = device.createBuffer({ size: 27 * 4, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });

    this.bRefl = device.createBuffer({ size: P * 3 * 4, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });

    const lut = fgLut.lut;
    this.fgRes = fgLut.res;
    this.bFg = device.createBuffer({ size: lut.byteLength, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });
    device.queue.writeBuffer(this.bFg, 0, lut);

    // u8-unorm RGBA words: P*4 bytes (was P*12 as fp32 RGB — 3× smaller readback).
    this.bOut = device.createBuffer({ size: P * 4, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC });
    // Single readback staging buffer + its free→inflight→ready state. The shade
    // pipeline is strictly serial (HairRenderer's `_shadeInFlight` gate holds off
    // the next shade until this one's readback is applied), so exactly one
    // readback is ever in flight.
    this._rbBuf = device.createBuffer({ size: P * 4, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST });
    this._rbState = 'free'; // 'free' | 'inflight' | 'ready'

    this._buildBind0(this.bRefl);
    this.ready = true;
  }

  // The stride-10 static-attr buffer (matModUnpacker writes albedo lanes here).
  staticBuffer() { return this.bStatic; }

  // (Re)build bind group 0 with the given reflectance buffer — either the
  // shader-owned bRefl (CPU 3ch path) or ORT's bsdf output gpu-buffer (1ch).
  _buildBind0(reflBuffer) {
    this.bind0 = this.device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.uParams } },
        { binding: 1, resource: { buffer: this.bSh27 } },
        { binding: 2, resource: { buffer: this.bStatic } },
        { binding: 3, resource: { buffer: reflBuffer } },
        { binding: 4, resource: { buffer: this.bFg } },
        { binding: 5, resource: { buffer: this.bOut } },
      ],
    });
    this._reflBound = reflBuffer;
  }

  // opts: { specGpuBuffer, sh27, campos:[x,y,z], diffuseScale, specScale,
  //         useNeuralBsdf, N, nPad, plane, reflVersion, reflGpuBuffer, reflCh,
  //         reflectance }
  // Dispatches the GPU shade and kicks off an async readback; does NOT block on
  // the result. The packed colours land later via pollApply(). Returns immediately.
  async shade(opts) {
    const device = this.device;

    // Per-frame uniforms. This 12-word layout is a hard contract with the
    // `Params` struct in shade.wgsl — the word index MUST match, and word 3
    // packs diffuseScale into campos's 4th lane (WGSL vec3<f32> occupies 16 B).
    // Word → field: 0-2 campos.xyz (f32) · 3 diffuseScale (f32) · 4 specScale
    // (f32) · 5 useNeuralBsdf (u32) · 6 N · 7 nPad · 8 plane · 9 P · 10 fgRes ·
    // 11 reflCh. Add a field only in lockstep with the struct or colours corrupt.
    this._uf[0] = opts.campos[0]; this._uf[1] = opts.campos[1]; this._uf[2] = opts.campos[2];
    this._uf[3] = opts.diffuseScale;
    this._uf[4] = opts.specScale;
    this._uu[5] = opts.useNeuralBsdf ? 1 : 0;
    this._uu[6] = opts.N;
    this._uu[7] = opts.nPad;
    this._uu[8] = opts.plane;
    this._uu[9] = this.P;
    this._uu[10] = this.fgRes;
    this._uu[11] = opts.reflCh ?? 3;
    device.queue.writeBuffer(this.uParams, 0, this._u);
    device.queue.writeBuffer(this.bSh27, 0, opts.sh27, 0, 27);

    // Reflectance only when it changed (NeuralBSDF re-ran). Two sources:
    //  - gpu-resident bsdf output: bind ORT's buffer directly (no data moves);
    //  - CPU 3ch array: upload into the shader-owned bRefl.
    if (opts.reflGpuBuffer) {
      if (this._reflBound !== opts.reflGpuBuffer) this._buildBind0(opts.reflGpuBuffer);
      this._reflVersion = opts.reflVersion;
    } else {
      if (this._reflBound !== this.bRefl) this._buildBind0(this.bRefl);
      if (opts.reflVersion !== this._reflVersion) {
        device.queue.writeBuffer(this.bRefl, 0, opts.reflectance);
        this._reflVersion = opts.reflVersion;
      }
    }

    // spec_mod output buffer can change per run → rebind group 1 when it does.
    // CONTRACT: specGpuBuffer, reflGpuBuffer (bsdf), and the mat_mod output the
    // unpack reads are all fp32-IO gpu-buffers — the WGSL reads them as
    // array<f32> and every buffer size here hardcodes a *4 byte stride. The fp16
    // decoders keep fp32 IO via Cast (keep_io_types), so this holds; flipping any
    // of these OUTPUTS to an fp16 gpu-buffer would corrupt silently (no size
    // mismatch to catch it). Keep decoder outputs fp32.
    if (this._specBuffer !== opts.specGpuBuffer) {
      this.bind1 = device.createBindGroup({
        layout: this.pipeline.getBindGroupLayout(1),
        entries: [{ binding: 0, resource: { buffer: opts.specGpuBuffer } }],
      });
      this._specBuffer = opts.specGpuBuffer;
    }

    const enc = device.createCommandEncoder({ label: 'hair-shade' });
    const pass = enc.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind0);
    pass.setBindGroup(1, this.bind1);
    pass.dispatchWorkgroups(Math.min(Math.ceil(this.P / 64), MAX_WG));
    pass.end();
    // Readback into the single staging buffer, then start the async map. The
    // serial gate guarantees it's 'free' here; if a prior readback somehow hasn't
    // been applied yet, skip the copy (this shade's colors are coalesced into the
    // next readback). The copy is queue-ordered against the dispatch above, so
    // bOut is coherent.
    const canReadback = this._rbState === 'free';
    if (canReadback) enc.copyBufferToBuffer(this.bOut, 0, this._rbBuf, 0, this.P * 4);
    device.queue.submit([enc.finish()]);
    if (canReadback) {
      this._rbState = 'inflight';
      this._rbBuf.mapAsync(GPUMapMode.READ)
        .then(() => { if (this._rbState === 'inflight') this._rbState = 'ready'; })
        .catch(() => { this._rbState = 'free'; });
    }
  }

  // Called every frame with the mesh's colour words (a Uint32Array view over
  // three's RGBA8 colour attribute). When the readback
  // has landed, merges it (RGB from the shade, alpha byte preserved) and returns
  // true (caller flags needsUpdate); then frees the buffer for the next shade.
  // apply=false frees the landed buffer WITHOUT writing — used while a
  // non-'shaded' debug view is active so shaded readbacks don't stomp it.
  pollApply(packedArray, apply = true) {
    if (this._rbState !== 'ready') return false;
    let consumed = false;
    if (apply) {
      mergePackedRgb(packedArray, new Uint32Array(this._rbBuf.getMappedRange()), this.P);
      consumed = true;
    }
    try { this._rbBuf.unmap(); } catch { /* ignore */ }
    this._rbState = 'free';
    return consumed;
  }

  // True while a dispatched shade's colours have not yet been applied (the serial
  // gate that keeps exactly one shade in flight).
  get pending() { return this._rbState !== 'free'; }

  // True when the shade readback has landed (ready to consume). Read BEFORE
  // pollApply (which frees the buffer) so the caller can reveal on arrival even
  // while a debug view suppresses the merge.
  hasLanded() { return this._rbState === 'ready'; }

  dispose() {
    for (const b of [this.uParams, this.bSh27, this.bStatic, this.bRefl, this.bFg, this.bOut, this._rbBuf]) {
      try { b?.destroy?.(); } catch { /* ignore */ }
    }
    this._rbBuf = null;
    this.bStatic = null;
    this.ready = false;
    this.device = this.pipeline = this.bind0 = this.bind1 = this._specBuffer = null;
  }
}
